import { getFeatureFlag } from '@/app/services/features';
import { getAccessToken } from '@/lib/tokenStorage';
import {
  IN_CLINIC_URL_FLAG,
  resolveInClinicApiUrl,
} from '@/pages/in-clinic/inClinicPath';
import type {
  AddRecipToQueueInput,
  ChangeEmbryoOwnerInput,
  CreateDonorInput,
  CreateSireInput,
  DataEntryRecord,
  Embryo,
  EmbryoOwner,
  HeatNotUsedInput,
  ImplantCidrInput,
  ImplantEmbryoInput,
  KickOutComment,
  LookupItem,
  OutsideEmbryoInput,
  PCInput,
  Recipient,
  RecipProcedure,
  RecipQueueItem,
  SearchResult,
} from '@/pages/in-clinic/types';

import { enhancedApi } from '../api/baseApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

async function fetchApi<T>(
  url: string,
  options: RequestInit = {},
): Promise<{ data: T } | { error: { status: number | string; data: string } }> {
  try {
    const token = getAccessToken();
    const resolvedUrl = resolveInClinicApiUrl(
      url,
      getFeatureFlag(IN_CLINIC_URL_FLAG),
    );
    const response = await fetch(`${API_BASE_URL}${resolvedUrl}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
        ...options.headers,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.text();
      return { error: { status: response.status, data: error } };
    }

    if (response.status === 204) {
      return { data: undefined as unknown as T };
    }

    const data: T = await response.json();
    return { data };
  } catch (error: any) {
    return { error: { status: 'FETCH_ERROR', data: error.message } };
  }
}

export const inClinicApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    getDrugs: build.query<LookupItem[], void>({
      queryFn: async () => fetchApi<LookupItem[]>('/api/in-clinic/drugs'),
    }),

    getVets: build.query<LookupItem[], void>({
      queryFn: async () => fetchApi<LookupItem[]>('/api/in-clinic/vets'),
      providesTags: ['InClinicLookups'],
    }),

    getTechs: build.query<LookupItem[], void>({
      queryFn: async () => fetchApi<LookupItem[]>('/api/in-clinic/techs'),
      providesTags: ['InClinicLookups'],
    }),

    getKickOutComments: build.query<KickOutComment[], void>({
      queryFn: async () =>
        fetchApi<KickOutComment[]>('/api/in-clinic/kick-out-comments'),
    }),

    getEmbryos: build.query<Embryo[], { date: string; siteId: string }>({
      queryFn: async ({ date, siteId }) => {
        const params = new URLSearchParams({ date, siteId });
        return fetchApi<Embryo[]>(`/api/in-clinic/embryos?${params}`);
      },
      providesTags: ['InClinicEmbryos'],
    }),

    getFrozenEmbryos: build.query<Embryo[], string>({
      queryFn: async (cane) => {
        const params = new URLSearchParams({ cane });
        return fetchApi<Embryo[]>(`/api/in-clinic/embryos/frozen?${params}`);
      },
      providesTags: ['InClinicFrozenEmbryos'],
    }),

    getEmbryoDetail: build.query<Embryo, string>({
      queryFn: async (embryoId) =>
        fetchApi<Embryo>(`/api/in-clinic/embryos/${embryoId}`),
    }),

    getEmbryoOwners: build.query<EmbryoOwner[], string>({
      queryFn: async (embryoId) =>
        fetchApi<EmbryoOwner[]>(`/api/in-clinic/embryos/${embryoId}/owners`),
    }),

    getRecips: build.query<Recipient[], string>({
      queryFn: async (yardId) => {
        const params = new URLSearchParams({ yardId });
        return fetchApi<Recipient[]>(`/api/in-clinic/recips?${params}`);
      },
      providesTags: ['InClinicRecips'],
    }),

    getRecipsInQueue: build.query<
      RecipQueueItem[],
      { yardId: string; chute: number }
    >({
      queryFn: async ({ yardId, chute }) => {
        const params = new URLSearchParams({
          yardId,
          chute: chute.toString(),
        });
        return fetchApi<RecipQueueItem[]>(
          `/api/in-clinic/recips/queue?${params}`,
        );
      },
      providesTags: ['InClinicRecipQueue'],
    }),

    getRecipProcHistory: build.query<RecipProcedure[], string>({
      queryFn: async (recipId) =>
        fetchApi<RecipProcedure[]>(
          `/api/in-clinic/recips/${recipId}/procedures`,
        ),
      providesTags: ['InClinicRecipProcs'],
    }),

    getDataEntry: build.query<
      DataEntryRecord,
      {
        embryoId: string;
        yard: string;
        chute: number;
        recipName: string;
      }
    >({
      queryFn: async ({ embryoId, yard, chute, recipName }) => {
        const params = new URLSearchParams({
          embryoId,
          yard,
          chute: chute.toString(),
          recipName,
        });
        return fetchApi<DataEntryRecord>(`/api/in-clinic/data-entry?${params}`);
      },
      providesTags: ['InClinicEmbryos'],
    }),

    searchImplantAccounts: build.query<SearchResult[], string>({
      queryFn: async (q) => {
        const params = new URLSearchParams({ q });
        return fetchApi<SearchResult[]>(
          `/api/in-clinic/search/accounts?${params}`,
        );
      },
    }),

    searchImplantSires: build.query<SearchResult[], string>({
      queryFn: async (q) => {
        const params = new URLSearchParams({ q });
        return fetchApi<SearchResult[]>(
          `/api/in-clinic/search/sires?${params}`,
        );
      },
    }),

    searchImplantDonors: build.query<
      SearchResult[],
      { q: string; account?: string }
    >({
      queryFn: async ({ q, account }) => {
        const params = new URLSearchParams({ q });
        if (account) params.append('account', account);
        return fetchApi<SearchResult[]>(
          `/api/in-clinic/search/donors?${params}`,
        );
      },
    }),

    addRecipToQueue: build.mutation<void, AddRecipToQueueInput>({
      queryFn: async (input) =>
        fetchApi<void>('/api/in-clinic/recips/queue', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      invalidatesTags: ['InClinicRecipQueue', 'InClinicRecips'],
    }),

    removeRecipFromQueue: build.mutation<void, number>({
      queryFn: async (queueId) =>
        fetchApi<void>(`/api/in-clinic/recips/queue/${queueId}`, {
          method: 'DELETE',
        }),
      invalidatesTags: ['InClinicRecipQueue'],
    }),

    clearRecipQueue: build.mutation<
      { removed: number },
      { yardId: string; chute: number }
    >({
      queryFn: async ({ yardId, chute }) => {
        const params = new URLSearchParams({
          yardId,
          chute: chute.toString(),
        });
        return fetchApi<{ removed: number }>(
          `/api/in-clinic/recips/queue?${params}`,
          { method: 'DELETE' },
        );
      },
      invalidatesTags: ['InClinicRecipQueue'],
    }),

    implantEmbryo: build.mutation<void, ImplantEmbryoInput>({
      queryFn: async (input) =>
        fetchApi<void>('/api/in-clinic/implant', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      invalidatesTags: [
        'InClinicEmbryos',
        'InClinicRecipQueue',
        'InClinicRecipProcs',
      ],
    }),

    pcOpen: build.mutation<void, PCInput>({
      queryFn: async (input) =>
        fetchApi<void>('/api/in-clinic/pc-open', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      invalidatesTags: [
        'InClinicEmbryos',
        'InClinicRecipQueue',
        'InClinicRecipProcs',
      ],
    }),

    pcPreg: build.mutation<void, PCInput>({
      queryFn: async (input) =>
        fetchApi<void>('/api/in-clinic/pc-preg', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      invalidatesTags: [
        'InClinicEmbryos',
        'InClinicRecipQueue',
        'InClinicRecipProcs',
      ],
    }),

    heatNotUsed: build.mutation<void, HeatNotUsedInput>({
      queryFn: async (input) =>
        fetchApi<void>('/api/in-clinic/heat-not-used', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      invalidatesTags: [
        'InClinicRecipQueue',
        'InClinicRecipProcs',
        'InClinicRecips',
      ],
    }),

    implantCidr: build.mutation<void, ImplantCidrInput>({
      queryFn: async (input) =>
        fetchApi<void>('/api/in-clinic/implant-cidr', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      invalidatesTags: ['InClinicRecipProcs'],
    }),

    changeEmbryoOwner: build.mutation<void, ChangeEmbryoOwnerInput>({
      queryFn: async ({ embryoId, newOwnerId }) =>
        fetchApi<void>(`/api/in-clinic/embryos/${embryoId}/owner`, {
          method: 'PUT',
          body: JSON.stringify({ newOwnerId }),
        }),
      invalidatesTags: ['InClinicEmbryos'],
    }),

    createDonor: build.mutation<SearchResult, CreateDonorInput>({
      queryFn: async (input) =>
        fetchApi<SearchResult>('/api/in-clinic/donors', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
    }),

    createSire: build.mutation<SearchResult, CreateSireInput>({
      queryFn: async (input) =>
        fetchApi<SearchResult>('/api/in-clinic/sires', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
    }),

    kickOutRecip: build.mutation<void, PCInput>({
      queryFn: async (input) =>
        fetchApi<void>('/api/in-clinic/recips/kick-out', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      invalidatesTags: [
        'InClinicEmbryos',
        'InClinicRecipQueue',
        'InClinicRecipProcs',
        'InClinicRecips',
      ],
    }),

    createOutsideEmbryo: build.mutation<void, OutsideEmbryoInput>({
      queryFn: async (input) =>
        fetchApi<void>('/api/in-clinic/outside-embryos', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      invalidatesTags: ['InClinicEmbryos'],
    }),
  }),
});

export const {
  useGetDrugsQuery,
  useGetVetsQuery,
  useGetTechsQuery,
  useGetKickOutCommentsQuery,
  useGetEmbryosQuery,
  useGetFrozenEmbryosQuery,
  useGetEmbryoDetailQuery,
  useGetEmbryoOwnersQuery,
  useGetRecipsQuery,
  useGetRecipsInQueueQuery,
  useGetRecipProcHistoryQuery,
  useGetDataEntryQuery,
  useSearchImplantAccountsQuery,
  useLazySearchImplantAccountsQuery,
  useSearchImplantSiresQuery,
  useSearchImplantDonorsQuery,
  useAddRecipToQueueMutation,
  useRemoveRecipFromQueueMutation,
  useClearRecipQueueMutation,
  useImplantEmbryoMutation,
  usePcOpenMutation,
  usePcPregMutation,
  useHeatNotUsedMutation,
  useImplantCidrMutation,
  useChangeEmbryoOwnerMutation,
  useCreateDonorMutation,
  useCreateSireMutation,
  useKickOutRecipMutation,
  useCreateOutsideEmbryoMutation,
} = inClinicApi;
